import React from 'react';
import { useNavigate } from 'react-router-dom';
import BrandLogo from './BrandLogo';
import './Landing.css';

const features = [
  ['01', 'Live recording', 'Record lectures, meetings, and voice memos, then turn them into useful notes automatically.', 'Unlimited recording · Auto transcription'],
  ['02', 'Video transcription', 'Convert YouTube links and uploaded videos into searchable text, summaries, and key points.', 'YouTube · MP4 · WebM · MOV'],
  ['03', 'AI suggestions', 'Expand a thought, improve clarity, or find the next useful idea without leaving your note.', 'Context-aware · One-click insertion'],
  ['04', 'Focused editing', 'Write with headings, lists, images, and rich formatting in an editor designed to stay out of your way.', 'Rich text · Automatic saving'],
  ['05', 'Smart organization', 'Use categories, favorites, color, and fast search to keep everything easy to find.', 'Categories · Favorites · Search'],
  ['06', 'Private by design', 'Keep control of your knowledge with secure authentication and a self-hostable architecture.', 'Secure sessions · Self-hostable'],
];

const audiences = [
  ['Students', 'Turn lectures and learning videos into clear, searchable study notes.'],
  ['Professionals', 'Capture meetings, webinars, decisions, and project context in one place.'],
  ['Creators', 'Move from recorded ideas to structured outlines and polished source material.'],
  ['Researchers', 'Organize interviews, references, and observations without losing the original context.'],
];

function Landing() {
  const navigate = useNavigate();

  return (
    <div className="landing-page">
      <a className="landing-skip" href="#main-content">Skip to main content</a>
      <header className="landing-header">
        <div className="landing-header__inner">
          <BrandLogo onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} title="Back to top" />
          <nav className="landing-nav" aria-label="Account navigation">
            <button className="landing-button landing-button--quiet" onClick={() => navigate('/login')}>Log in</button>
            <button className="landing-button landing-button--primary landing-nav__cta" onClick={() => navigate('/register')}>Get started</button>
          </nav>
        </div>
      </header>

      <main id="main-content">
        <section className="landing-hero" aria-labelledby="hero-title">
          <div className="landing-hero__copy">
            <p className="landing-eyebrow">Capture less. Understand more.</p>
            <h1 id="hero-title">Turn every conversation into knowledge.</h1>
            <p className="landing-hero__lede">Record, transcribe, and organize your ideas in one focused workspace—with AI ready when you need it.</p>
            <div className="landing-hero__actions">
              <button className="landing-button landing-button--primary landing-button--large" onClick={() => navigate('/register')}>Start free today</button>
              <a className="landing-button landing-button--outline landing-button--large" href="#features">Explore features</a>
            </div>
            <p className="landing-hero__note">Free and open source · No credit card required</p>
          </div>

          <div className="landing-preview" aria-label="Cloudnotes workspace preview">
            <div className="landing-preview__bar">
              <span className="landing-preview__status">Live workspace</span>
              <span className="landing-preview__saved">Saved</span>
            </div>
            <div className="landing-preview__body">
              <aside className="landing-preview__rail" aria-hidden="true"><span className="is-active" /><span /><span /><span /></aside>
              <article className="landing-preview__note">
                <p className="landing-preview__label">MEETING NOTE</p>
                <h2>Product research sync</h2>
                <p>We found three patterns worth carrying into the next iteration:</p>
                <ul>
                  <li>Reduce friction at the point of capture</li>
                  <li>Keep source context beside every insight</li>
                  <li>Make the next action immediately clear</li>
                </ul>
                <div className="landing-preview__ai">
                  <span aria-hidden="true">AI</span>
                  <p><strong>Suggested next step</strong><br />Turn these patterns into a testable product brief.</p>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="landing-section landing-features" id="features" aria-labelledby="features-title">
          <div className="landing-section__heading">
            <p className="landing-eyebrow">One knowledge workspace</p>
            <h2 id="features-title">Everything you need to capture and create</h2>
            <p>From the first spoken thought to the note you return to months later.</p>
          </div>
          <div className="landing-feature-grid">
            {features.map(([number, title, description, detail]) => (
              <article className="landing-feature-card" key={number}>
                <span className="landing-feature-card__number">{number}</span>
                <h3>{title}</h3><p>{description}</p><small>{detail}</small>
              </article>
            ))}
          </div>
        </section>

        <section className="landing-section landing-audiences" aria-labelledby="audiences-title">
          <div className="landing-section__heading landing-section__heading--left">
            <p className="landing-eyebrow">Built for deep work</p>
            <h2 id="audiences-title">Your ideas, finally connected</h2>
            <p>Cloudnotes adapts to the way you learn, work, make, and discover.</p>
          </div>
          <div className="landing-audience-grid">
            {audiences.map(([title, description]) => <article className="landing-audience-card" key={title}><h3>{title}</h3><p>{description}</p></article>)}
          </div>
        </section>

        <section className="landing-technology" aria-label="Technology">
          <p>Built with</p>
          <ul><li>React 18</li><li>Flask + Python</li><li>OpenAI</li><li>PostgreSQL</li><li>Docker</li></ul>
        </section>

        <section className="landing-cta" aria-labelledby="cta-title">
          <div>
            <p className="landing-eyebrow landing-eyebrow--cyan">Your next idea belongs here</p>
            <h2 id="cta-title">Make your knowledge useful.</h2>
            <p>Start free, stay in control, and bring AI in when it helps.</p>
          </div>
          <button className="landing-button landing-button--cyan landing-button--large" onClick={() => navigate('/register')}>Create your workspace</button>
        </section>
      </main>

      <footer className="landing-footer">
        <div className="landing-footer__top">
          <div><BrandLogo bare /><p>AI-powered knowledge capture for clearer thinking.</p></div>
          <nav aria-label="Footer navigation">
            <a href="#features">Features</a>
            <button onClick={() => navigate('/login')}>Log in</button>
            <button onClick={() => navigate('/register')}>Sign up</button>
          </nav>
        </div>
        <div className="landing-footer__bottom">
          <span>© 2026 Cloudnotes</span><strong>A product of Inframodule</strong><span>Free and open source · MIT License</span>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
